import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAgenticLogicRule } from "@src/api/agentic-logic/get-agentic-logic-rule";
import { cancelActiveAgenticLogicCreation } from "@src/features/ai/components/AiChat/agenticLogicCreationController";
import { useGetChatHistory } from "@src/api/ai/chat-history/use-get-chat-history";
import {
  type AgenticLogicSurface,
  isAgenticLogicPathname,
  resolveAgenticLogicView,
} from "@src/features/ai/pages/agentic-logic/surface/surface";
import { useDispatch, useSelector } from "@src/store";
import {
  clearAiChat,
  getAiChat,
  restoreChatState,
} from "@src/store/slices/aiChat";
import { getAiChatProps, setAiChat } from "@src/store/slices/global";

/**
 * Keeps the Atlas chat rail open on a rule's workspace, and rehydrates the
 * conversation that authored the rule. With no active build the panel is an
 * idle chat; the first message starts a code-entry rule creation session (see
 * `AgenticLogicCreationChat`). The panel is always the inline rail, never a
 * modal (`AiChat` disables the below-`lg` dialog for this mode), so forcing it
 * open never blocks the page.
 *
 * The creation view is deliberately not pinned: it opens the chat itself when
 * a build starts — Ask Atlas or a template card (see `CreateRuleTab`) — so the
 * page shows its entry points, not an empty chat. A session started there
 * stays open (`isAtlasChatLocation` still spans both views); this hook only
 * decides where an idle rail is forced open. The rules list isn't pinned
 * either: it is a browse view whose table runs the full width.
 *
 * A chat already open in another mode is left alone rather than having its
 * transcript destroyed; Atlas takes the panel once that chat is closed.
 *
 * REHYDRATION. The conversation is durable server-side, and the rule row now
 * names the session that authored it, so opening a rule loads that transcript
 * back into the live chat rather than an empty one — the merchant keeps what
 * Atlas said, most importantly the diagnosis of a build that gave up. It is
 * restored into redux (not injected read-only) so the next message continues
 * the same session. Three states degrade rather than fail:
 *   - a rule with no authoring session (created before the association was
 *     recorded, or outside a chat) opens the empty chat exactly as before;
 *   - a transcript that fails to load leaves the empty chat, because the rail
 *     is still usable without it;
 *   - a chat that already carries turns is never overwritten, so an in-flight
 *     build survives navigation between rules.
 * The restore is attempted once per rule per mount, which is what lets "New
 * chat" clear the rail without it immediately refilling.
 *
 * Leaving the Atlas chat locations is what closes the panel — see the
 * pathname effect in `LoggedInProvider`.
 */
export const useAtlasChatRail = (surface: AgenticLogicSurface) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { buildingRuleId, open, mode } = useSelector(getAiChatProps);
  const aiChat = useSelector(getAiChat);
  const view = resolveAgenticLogicView(search, surface.ruleParam);
  // An embedded surface has no route of its own — its host is a dialog, and
  // this hook only runs while that dialog has mounted the provider — so the
  // view alone decides. Standalone surfaces keep the pathname check.
  const isOnSurface =
    surface.kind === "embedded" || isAgenticLogicPathname(pathname);
  // The creation view is pinned only when embedded. The standalone create page
  // carries its own Ask Atlas composer and opens the rail itself when a build
  // starts, so pinning would show an empty chat beside a form that already
  // offers one; the embedded view drops that composer precisely because the
  // rail is the composer there, so it has to be on screen from the start.
  const isPinned =
    isOnSurface &&
    (view.kind === "rule" ||
      (surface.kind === "embedded" && view.kind === "new"));
  const ruleId = view.kind === "rule" ? view.ruleId : null;

  useEffect(() => {
    if (!isPinned) {
      return;
    }
    // An open chat already owns the panel — whether it is Atlas's own session
    // or another mode's. Only a closed (or mode-less) panel is taken over.
    if (open && mode !== undefined) {
      return;
    }

    dispatch(clearAiChat());
    dispatch(
      setAiChat([
        { key: "open", value: true },
        { key: "mode", value: "agentic-logic-creation" },
        { key: "initialContext", value: null },
        { key: "start", value: false },
        { key: "buildingRuleId", value: null },
        { key: "activeScenario", value: null },
      ])
    );
  }, [dispatch, isPinned, mode, open]);

  // The session whose transcript is being fetched, and the rule it was asked
  // for. The second half is the once-per-rule guard: without it, clearing the
  // rail ("New chat") would satisfy the empty-chat condition again and refill
  // it with the transcript the merchant just dismissed.
  const [sessionToLoad, setSessionToLoad] = useState<string | null>(null);
  const attemptedForRuleId = useRef<string | null>(null);

  // The rule whose conversation the rail is showing. A conversation belongs to
  // ONE rule: it is that rule's authoring transcript, its questions are about
  // that rule, and the carts offered beside the composer run against it. So
  // moving to another rule starts that rule's conversation instead of carrying
  // the last one across, where every part of it would be about the wrong rule.
  //
  // Only a move BETWEEN rules clears. The first rule the rail sees is adopted
  // silently, which is what keeps the create flow's `?rule=new` → `?rule=<id>
  // hand-off — the moment the build's own transcript lands on the workspace —
  // from wiping itself.
  //
  // The new-rule view starts a new authoring lifecycle, so it also resets the
  // remembered rule. The rules list does not: rule-1 → list → rule-2 is still
  // a move between existing rules and must clear rule-1's conversation.
  const shownRuleId = useRef<string | null>(null);
  useEffect(() => {
    if (view.kind === "new") {
      shownRuleId.current = null;
      return;
    }
    if (!isPinned || !ruleId) {
      return;
    }
    const previous = shownRuleId.current;
    shownRuleId.current = ruleId;
    if (previous === null || previous === ruleId || buildingRuleId === ruleId) {
      return;
    }

    // The stream first: a run still writing into the panel would repopulate
    // the rail we are about to clear, mixing the old rule's turns into the new
    // rule's conversation.
    cancelActiveAgenticLogicCreation("rule-change");
    dispatch(clearAiChat());
    dispatch(
      setAiChat([
        { key: "initialContext", value: null },
        { key: "start", value: false },
        { key: "buildingRuleId", value: null },
        { key: "activeScenario", value: null },
      ])
    );
    // Let the new rule's own transcript load, and drop any request for the old
    // one still in flight — it would otherwise land on the empty panel this
    // just produced.
    attemptedForRuleId.current = null;
    setSessionToLoad(null);
  }, [buildingRuleId, dispatch, isPinned, ruleId, view.kind]);

  const ruleQuery = useAgenticLogicRule(
    isPinned && ruleId ? ruleId : undefined
  );
  const authoringSessionId = ruleQuery.data?.rule.authoringSessionId ?? null;

  const isIdleChat = aiChat.turns.length === 0 && !aiChat.sessionId;

  useEffect(() => {
    if (!isPinned || !ruleId || !authoringSessionId) {
      return;
    }
    if (attemptedForRuleId.current === ruleId) {
      return;
    }
    if (!isIdleChat) {
      return;
    }

    attemptedForRuleId.current = ruleId;
    setSessionToLoad(authoringSessionId);
  }, [authoringSessionId, isIdleChat, isPinned, ruleId]);

  const sessionHistory = useGetChatHistory({
    sessionId: sessionToLoad,
    enabled: sessionToLoad !== null,
  });

  useEffect(() => {
    if (sessionToLoad === null || !sessionHistory.isSuccess) {
      return;
    }
    const chat = sessionHistory.data?.chat ?? null;
    // Restored only onto a chat that is still empty: the fetch is async, and a
    // merchant who started typing meanwhile owns the panel.
    if (chat && isIdleChat) {
      dispatch(restoreChatState(chat));
    }
    setSessionToLoad(null);
  }, [
    aiChat.sessionId,
    aiChat.turns.length,
    dispatch,
    isIdleChat,
    sessionHistory.data,
    sessionHistory.isSuccess,
    sessionToLoad,
  ]);
};
